(() => {
	const globalKey = 'FlowrodsPeopleStoriesCarousel';

	if (window[globalKey]) {
		window[globalKey].init(document);
		return;
	}

	class PeopleStoriesCarousel {
		constructor(section) {
			this.section = section;
			this.track = section.querySelector('.flowrods-people-stories__track');
			this.cards = Array.from(section.querySelectorAll('.flowrods-person-story'));
			this.enabled = section.dataset.autoScroll === 'true';
			this.interval = Math.max(Number(section.dataset.autoScrollInterval) || 4000, 3000);
			this.pauseReasons = new Set();
			this.timer = null;
			this.visible = false;
			this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

			if (!this.track || this.cards.length < 2) return;

			this.onMouseEnter = () => this.pause('hover');
			this.onMouseLeave = () => this.resume('hover');
			this.onFocusIn = () => this.pause('focus');
			this.onFocusOut = (event) => {
				if (!this.track.contains(event.relatedTarget)) this.resume('focus');
			};
			this.onPointerDown = () => this.pause('interaction');
			this.onPointerUp = () => this.resume('interaction');
			this.onVisibilityChange = () => this.schedule();
			this.onMotionChange = () => this.schedule();
			this.onBlockSelect = () => this.pause('editor');
			this.onBlockDeselect = () => this.resume('editor');

			this.track.addEventListener('mouseenter', this.onMouseEnter);
			this.track.addEventListener('mouseleave', this.onMouseLeave);
			this.track.addEventListener('focusin', this.onFocusIn);
			this.track.addEventListener('focusout', this.onFocusOut);
			this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
			window.addEventListener('pointerup', this.onPointerUp, { passive: true });
			window.addEventListener('pointercancel', this.onPointerUp, { passive: true });
			document.addEventListener('visibilitychange', this.onVisibilityChange);
			this.section.addEventListener('shopify:block:select', this.onBlockSelect);
			this.section.addEventListener('shopify:block:deselect', this.onBlockDeselect);

			if (this.reducedMotion.addEventListener) {
				this.reducedMotion.addEventListener('change', this.onMotionChange);
			} else {
				this.reducedMotion.addListener(this.onMotionChange);
			}

			this.observer = new IntersectionObserver(
				(entries) => {
					this.visible = entries.some((entry) => entry.isIntersecting);
					this.schedule();
				},
				{ threshold: 0.35 },
			);
			this.observer.observe(this.section);
		}

		canPlay() {
			return (
				this.enabled &&
				this.visible &&
				!document.hidden &&
				!this.reducedMotion.matches &&
				this.pauseReasons.size === 0 &&
				this.track.scrollWidth > this.track.clientWidth + 2
			);
		}

		getStep() {
			if (this.cards.length > 1) {
				return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
			}
			return this.cards[0].offsetWidth;
		}

		advance() {
			const maxScroll = this.track.scrollWidth - this.track.clientWidth;
			const atEnd = this.track.scrollLeft >= maxScroll - 2;
			const target = atEnd ? 0 : Math.min(this.track.scrollLeft + this.getStep(), maxScroll);

			this.track.scrollTo({ left: target, behavior: 'smooth' });
		}

		schedule() {
			window.clearTimeout(this.timer);
			this.timer = null;

			if (!this.canPlay()) return;

			this.timer = window.setTimeout(() => {
				this.advance();
				this.schedule();
			}, this.interval);
		}

		pause(reason) {
			this.pauseReasons.add(reason);
			this.schedule();
		}

		resume(reason) {
			this.pauseReasons.delete(reason);
			this.schedule();
		}

		destroy() {
			window.clearTimeout(this.timer);
			this.observer?.disconnect();
			this.track?.removeEventListener('mouseenter', this.onMouseEnter);
			this.track?.removeEventListener('mouseleave', this.onMouseLeave);
			this.track?.removeEventListener('focusin', this.onFocusIn);
			this.track?.removeEventListener('focusout', this.onFocusOut);
			this.track?.removeEventListener('pointerdown', this.onPointerDown);
			window.removeEventListener('pointerup', this.onPointerUp);
			window.removeEventListener('pointercancel', this.onPointerUp);
			document.removeEventListener('visibilitychange', this.onVisibilityChange);
			this.section.removeEventListener('shopify:block:select', this.onBlockSelect);
			this.section.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

			if (this.reducedMotion.removeEventListener) {
				this.reducedMotion.removeEventListener('change', this.onMotionChange);
			} else {
				this.reducedMotion.removeListener(this.onMotionChange);
			}
		}
	}

	const init = (scope = document) => {
		const sections = [];

		if (scope.matches?.('[data-flowrods-people-stories]')) sections.push(scope);
		sections.push(...scope.querySelectorAll?.('[data-flowrods-people-stories]') || []);

		sections.forEach((section) => {
			if (section.flowrodsPeopleStoriesCarousel) return;
			section.flowrodsPeopleStoriesCarousel = new PeopleStoriesCarousel(section);
		});
	};

	const destroy = (scope) => {
		const section = scope.matches?.('[data-flowrods-people-stories]')
			? scope
			: scope.querySelector?.('[data-flowrods-people-stories]');

		section?.flowrodsPeopleStoriesCarousel?.destroy();
		if (section) delete section.flowrodsPeopleStoriesCarousel;
	};

	window[globalKey] = { init, destroy };

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
	} else {
		init(document);
	}

	document.addEventListener('shopify:section:load', (event) => init(event.target));
	document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
